/**
 * Shared credential helpers for device add/edit flows.
 *
 * Moved verbatim (parameterized on form values instead of closing over
 * component state) from DevicesPage so both the add-device flow and the
 * page's edit modal share one implementation.
 */
import React from 'react';
import { TextInput, PasswordInput, Checkbox } from '@mantine/core';
import { DeviceType } from '../../types/devices';
import { DeviceCredentials } from './constants';

/** The subset of add/edit form fields that feed credential shaping + validation. */
export interface CredentialFormValues {
  displayName: string;
  username: string;
  email: string;
  password: string;
  enableWaterTests: boolean;
  enableMaintenance: boolean;
  selectedAquaWizDevice: string | null;
}

/** Build the credentials object sent to the API for a given device type. */
export function buildCredentials(
  type: DeviceType,
  values: Pick<
    CredentialFormValues,
    'username' | 'email' | 'password' | 'enableWaterTests' | 'enableMaintenance' | 'selectedAquaWizDevice'
  >,
): DeviceCredentials {
  const { username, email, password, enableWaterTests, enableMaintenance, selectedAquaWizDevice } = values;
  switch (type) {
    case 'apex_fusion':
      return { username, password };
    case 'focustronic':
    case 'mastertronic':
    case 'alkatronic':
    case 'dosetronic':
      return { email, password };
    case 'kamoer_kh':
    case 'kamoer_stp':
    case 'kamoer_p4':
      return { email, password };
    case 'aquawiz':
      return {
        email,
        password,
        ...(selectedAquaWizDevice && { device_id: selectedAquaWizDevice }),
      };
    case 'aquarimate':
      return {
        email,
        password,
        enable_water_tests: enableWaterTests,
        enable_maintenance: enableMaintenance,
      };
    default:
      return {};
  }
}

/**
 * Validate the credential form for a device type.
 * Returns an error message string, or null when valid.
 */
export function validateForm(
  type: DeviceType,
  values: Pick<CredentialFormValues, 'displayName' | 'username' | 'email' | 'password'>,
  options?: { skipCredentials?: boolean },
): string | null {
  const { displayName, username, email, password } = values;

  // Display name not required for Focustronic or Kamoer (auto-generated from selected devices)
  if (
    type !== 'focustronic' &&
    type !== 'kamoer_kh' &&
    type !== 'kamoer_stp' &&
    type !== 'kamoer_p4' &&
    !displayName.trim()
  ) {
    return 'Display name is required';
  }

  // Skip credential validation for linked Kamoer devices (credentials managed by provider account)
  if (options?.skipCredentials) {
    return null;
  }

  switch (type) {
    case 'apex_fusion':
      if (!username.trim() || !password.trim()) {
        return 'Username and password are required';
      }
      break;
    case 'focustronic':
    case 'mastertronic':
    case 'alkatronic':
    case 'dosetronic':
    case 'aquawiz':
    case 'aquarimate':
    case 'kamoer_kh':
    case 'kamoer_stp':
    case 'kamoer_p4':
      if (!email.trim() || !password.trim()) {
        return 'Email and password are required';
      }
      break;
  }

  return null;
}

export interface CredentialFieldsProps {
  type: DeviceType;
  username: string;
  email: string;
  password: string;
  enableWaterTests: boolean;
  enableMaintenance: boolean;
  disabled?: boolean;
  onUsernameChange: (value: string) => void;
  onEmailChange: (value: string) => void;
  onPasswordChange: (value: string) => void;
  onEnableWaterTestsChange: (value: boolean) => void;
  onEnableMaintenanceChange: (value: boolean) => void;
}

/** Render the credential inputs appropriate for the given device type. */
export const CredentialFields: React.FC<CredentialFieldsProps> = ({
  type,
  username,
  email,
  password,
  enableWaterTests,
  enableMaintenance,
  disabled,
  onUsernameChange,
  onEmailChange,
  onPasswordChange,
  onEnableWaterTestsChange,
  onEnableMaintenanceChange,
}) => {
  switch (type) {
    case 'apex_fusion':
      return (
        <>
          <TextInput
            label="Username"
            placeholder="user@example.com"
            required
            value={username}
            onChange={(e) => onUsernameChange(e.target.value)}
            disabled={disabled}
          />
          <PasswordInput
            label="Password"
            placeholder="Enter password"
            required
            value={password}
            onChange={(e) => onPasswordChange(e.target.value)}
            disabled={disabled}
            autoComplete="new-password"
          />
        </>
      );
    case 'focustronic':
    case 'mastertronic':
    case 'alkatronic':
    case 'dosetronic':
    case 'aquawiz':
    case 'kamoer_kh':
    case 'kamoer_stp':
    case 'kamoer_p4':
      return (
        <>
          <TextInput
            label="Email"
            placeholder="user@example.com"
            required
            value={email}
            onChange={(e) => onEmailChange(e.target.value)}
            disabled={disabled}
            autoComplete="off"
          />
          <PasswordInput
            label="Password"
            placeholder="Enter password"
            required
            value={password}
            onChange={(e) => onPasswordChange(e.target.value)}
            disabled={disabled}
            autoComplete="new-password"
          />
        </>
      );
    case 'aquarimate':
      return (
        <>
          <TextInput
            label="Email"
            placeholder="user@example.com"
            required
            value={email}
            onChange={(e) => onEmailChange(e.target.value)}
            disabled={disabled}
            autoComplete="off"
          />
          <PasswordInput
            label="Password"
            placeholder="Enter password"
            required
            value={password}
            onChange={(e) => onPasswordChange(e.target.value)}
            disabled={disabled}
            autoComplete="new-password"
          />
          <Checkbox
            label="Enable Water Tests"
            description="Collect water test results from Aquarimate"
            checked={enableWaterTests}
            onChange={(e) => onEnableWaterTestsChange(e.currentTarget.checked)}
            disabled={disabled}
            mt="md"
          />
          <Checkbox
            label="Enable Maintenance Activities"
            description="Collect maintenance activity logs from Aquarimate"
            checked={enableMaintenance}
            onChange={(e) => onEnableMaintenanceChange(e.currentTarget.checked)}
            disabled={disabled}
            mt="xs"
          />
        </>
      );
    default:
      return null;
  }
};
